import {
  Alert,
  InteractiveContainer,
  PlainButton,
  Tooltip,
  Radio,
  ListBoxItem,
  ResourcePickerPopover,
} from "@jugl-web/ui-kit";
import { cx, useAutoResizedTextarea, useTranslations } from "@jugl-web/utils";
import {
  ChangeEvent,
  FC,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AgentConfigData,
  AgentInstructionOptionId,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION } from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import Lottie from "react-lottie";
import {
  getIntegrationTileState,
  groupConnectionsByType,
} from "@web-src/modules/cx/pages/CXAIAgentsPage/utils";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as ExpandIcon } from "./assets/expand.svg";
import { ReactComponent as ResetIcon } from "./assets/reset.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as CalendlyIcon } from "./assets/calendly.svg";
import { ReactComponent as CalcomIcon } from "./assets/calcom.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as SquareIcon } from "./assets/square.svg";
import brainAnimation from "./assets/brain.json";
import {
  CUSTOM_PROMPT_ID,
  isInstructionOptionId,
  normalizeInstructionItems,
  upsertInstructionItem,
} from "./utils";

export const AgentInstructionsPanel: FC<{
  variant?: "default" | "drawer";
  entityId: string;
  onAddAutomation: () => void;
  onRequestFullscreen?: () => void;
}> = ({
  variant = "default",
  entityId,
  onAddAutomation,
  onRequestFullscreen,
}) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const { data } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
    meta: { silent: true },
  });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();

  const isDrawer = variant === "drawer";
  const [businessName, setBusinessName] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const businessNameTextareaRef = useRef<HTMLTextAreaElement>(null);
  const instructionsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editorFieldsRef = useRef<HTMLDivElement>(null);

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [selectedPromptOptionId, setSelectedPromptOptionId] =
    useState<AgentInstructionOptionId>("custom-prompt");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isBusinessNameFocused, setIsBusinessNameFocused] = useState(false);

  const { data: allConnections } = integrationsApi.useGetConnectionsQuery({
    entityId,
  });

  const connectionsByType = useMemo(
    () => groupConnectionsByType(allConnections?.data),
    [allConnections?.data]
  );

  const calendlyTile = useMemo(
    () => getIntegrationTileState(connectionsByType.calendly),
    [connectionsByType]
  );
  const squareupTile = useMemo(
    () => getIntegrationTileState(connectionsByType.squareup),
    [connectionsByType]
  );
  const calcomTile = useMemo(
    () => getIntegrationTileState(connectionsByType.cal),
    [connectionsByType]
  );
  const isShopifyConnected = Boolean(connectionsByType.shopify?.length);
  const connectedAutomations = useMemo(() => {
    const items: { id: string; Icon: FC }[] = [];

    if (
      calendlyTile.isConnected &&
      !calendlyTile.needsSetup &&
      !calendlyTile.isExpired
    ) {
      items.push({ id: "calendly", Icon: CalendlyIcon });
    }
    if (
      calcomTile.isConnected &&
      !calcomTile.needsSetup &&
      !calcomTile.isExpired
    ) {
      items.push({ id: "cal", Icon: CalcomIcon });
    }
    if (isShopifyConnected) {
      items.push({ id: "shopify", Icon: ShopifyIcon });
    }
    if (
      squareupTile.isConnected &&
      !squareupTile.needsSetup &&
      !squareupTile.isExpired
    ) {
      items.push({ id: "squareup", Icon: SquareIcon });
    }

    return items;
  }, [calcomTile, calendlyTile, isShopifyConnected, squareupTile]);

  const connectedAutomationsCount = connectedAutomations.length;

  const isBusinessNameEmpty = !businessName.trim();

  const upsertAgentConfig = useCallback(
    async (configPatch: Partial<AgentConfigData>) => {
      if (!data) return;

      await updateAgentConfig({
        entityId,
        config: {
          ...data.config,
          ...configPatch,
        },
      }).unwrap();
    },
    [data, entityId, updateAgentConfig]
  );

  useEffect(() => {
    const normalizedInstructions = normalizeInstructionItems(
      data?.config.saved_instructions
    );
    const isSelectedOptionValid = isInstructionOptionId(
      data?.config.selected_instruction_option
    );
    const selectedOption = isSelectedOptionValid
      ? data.config.selected_instruction_option
      : CUSTOM_PROMPT_ID;
    const selectedFromList = normalizedInstructions.find(
      (item) => item.name === selectedOption
    );
    const selectedInstruction = normalizedInstructions[0] ?? {
      name: CUSTOM_PROMPT_ID,
      value: "",
    };
    const resolvedInstructionValue = isSelectedOptionValid
      ? (selectedFromList ?? selectedInstruction).value || ""
      : data?.config.instructions || "";

    setBusinessName(data?.config.business_name || "");
    setSelectedPromptOptionId(selectedOption);
    setInstructions(resolvedInstructionValue);
  }, [data]);

  const persistAgentConfig = useCallback(async () => {
    const trimmedBusinessName = businessName.trim();
    const trimmedInstructions = instructions.trim();

    setBusinessName(trimmedBusinessName);
    setInstructions(trimmedInstructions);

    const nextInstructions = upsertInstructionItem(
      data?.config.saved_instructions,
      selectedPromptOptionId,
      trimmedInstructions
    );

    const isDirty =
      trimmedBusinessName !== data?.config.business_name ||
      selectedPromptOptionId !== data?.config.selected_instruction_option ||
      trimmedInstructions !== data?.config.instructions ||
      JSON.stringify(nextInstructions) !==
        JSON.stringify(data?.config.saved_instructions);

    if (!isDirty) return;

    try {
      await upsertAgentConfig({
        business_name: trimmedBusinessName,
        instructions: trimmedInstructions,
        selected_instruction_option: selectedPromptOptionId,
        saved_instructions: nextInstructions,
      });
    } catch {
      // Error UI handled via meta + apiResultMiddleware
      setTimeout(() => {
        setBusinessName(trimmedBusinessName);
        setInstructions(trimmedInstructions);
      }, 10);
    }
  }, [
    data,
    businessName,
    instructions,
    selectedPromptOptionId,
    upsertAgentConfig,
  ]);

  const handleEditorBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      const next = e.relatedTarget;
      if (next instanceof Node && editorFieldsRef.current?.contains(next)) {
        return;
      }

      if (editorFieldsRef.current) {
        editorFieldsRef.current.scrollTop = 0;
      }

      persistAgentConfig();
    },
    [persistAgentConfig]
  );

  const { resize: resizeBusinessName } = useAutoResizedTextarea(
    businessNameTextareaRef
  );
  const { resize: resizeInstructions } = useAutoResizedTextarea(
    instructionsTextareaRef
  );

  const ensureInstructionsHeight = useCallback(() => {
    const scrollContainer = editorFieldsRef.current;
    const previousScrollTop = scrollContainer?.scrollTop ?? 0;

    resizeInstructions();
    const textarea = instructionsTextareaRef.current;
    if (textarea && textarea.scrollHeight > textarea.clientHeight) {
      textarea.style.height = `${textarea.scrollHeight}px`;
    }

    if (scrollContainer) {
      scrollContainer.scrollTop = previousScrollTop;
    }
  }, [resizeInstructions]);

  const handleInstructionsChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setInstructions(event.target.value);
      ensureInstructionsHeight();
    },
    [ensureInstructionsHeight]
  );

  useEffect(() => {
    resizeBusinessName();
  }, [businessName, resizeBusinessName]);

  useEffect(() => {
    if (isBusinessNameFocused) {
      businessNameTextareaRef.current?.focus();
    }
  }, [isBusinessNameFocused]);

  useEffect(() => {
    if (isDrawer) return undefined;

    const frameId = requestAnimationFrame(() =>
      requestAnimationFrame(ensureInstructionsHeight)
    );

    return () => cancelAnimationFrame(frameId);
  }, [instructions, isDrawer, ensureInstructionsHeight]);

  const predefiniedOptions = useMemo(
    (): ListBoxItem<{ title: string; instructions: string }>[] => [
      {
        id: "custom-prompt",
        value: {
          title: t({
            id: "cx-ai-agents-page.custom-prompt",
            defaultMessage: "Custom Prompt",
          }),
          instructions: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["custom-prompt"],
        },
      },
      {
        id: "customer-support-agent",
        value: {
          title: t({
            id: "cx-ai-agents-page.customer-support-agent",
            defaultMessage: "Customer Support Agent",
          }),
          instructions:
            DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["customer-support-agent"],
        },
      },
      {
        id: "service-agent",
        value: {
          title: t({
            id: "cx-ai-agents-page.service-agent",
            defaultMessage: "Service Agent",
          }),
          instructions: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["service-agent"],
        },
      },
      {
        id: "sales-agent",
        value: {
          title: t({
            id: "cx-ai-agents-page.sales-agent",
            defaultMessage: "Sales Agent",
          }),
          instructions: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["sales-agent"],
        },
      },
    ],
    [t]
  );

  const selectedPromptTitle = useMemo(() => {
    const option = predefiniedOptions.find(
      (item) => item.id === selectedPromptOptionId
    );
    return (
      option?.value.title ??
      t({
        id: "cx-ai-agents-page.custom-prompt",
        defaultMessage: "Custom Prompt",
      })
    );
  }, [predefiniedOptions, selectedPromptOptionId, t]);

  const isSelectedPromptAlreadyDefault =
    instructions.trim() ===
    DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION[
      isInstructionOptionId(selectedPromptOptionId)
        ? selectedPromptOptionId
        : CUSTOM_PROMPT_ID
    ].trim();

  const handlePromptSelect = useCallback(
    async (optionId: AgentInstructionOptionId) => {
      const nextInstructions = upsertInstructionItem(
        data?.config.saved_instructions,
        optionId
      );
      const selectedInstruction = nextInstructions.find(
        (item) => item.name === optionId
      ) ?? {
        name: optionId,
        value: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION[optionId],
      };

      setSelectedPromptOptionId(optionId);
      setInstructions(selectedInstruction.value);

      try {
        await upsertAgentConfig({
          instructions: selectedInstruction.value,
          selected_instruction_option: optionId,
          saved_instructions: nextInstructions,
        });
      } catch {
        // Error UI handled via meta + apiResultMiddleware
      }
    },
    [data?.config.saved_instructions, upsertAgentConfig]
  );

  const handleResetPrompt = useCallback(async () => {
    const defaultValue =
      DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION[selectedPromptOptionId];
    const nextInstructions = upsertInstructionItem(
      data?.config.saved_instructions,
      selectedPromptOptionId,
      defaultValue
    );

    setInstructions(defaultValue);

    try {
      await upsertAgentConfig({
        instructions: defaultValue,
        selected_instruction_option: selectedPromptOptionId,
        saved_instructions: nextInstructions,
      });
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    }
  }, [data, selectedPromptOptionId, upsertAgentConfig]);

  const promptPickerTrigger = (
    <ResourcePickerPopover
      items={predefiniedOptions}
      defaultSelectedIds={[selectedPromptOptionId]}
      selectionBehavior={{ mode: "single", canToggle: false }}
      renderLabel={(item) => item.value.title}
      renderEndIcon={(_, __, isSelected) => (
        <Radio readOnly isChecked={isSelected} size="sm" />
      )}
      onSelect={({ item, onClose }) => {
        handlePromptSelect(item.id as AgentInstructionOptionId);
        onClose();
      }}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          ref={triggerRef}
          as={InteractiveContainer}
          className={cx(
            "flex h-7 items-center gap-1 outline-none",
            isDrawer && "pt-0.5"
          )}
        >
          <span className={cx("text-xs text-dark-600", isOpen && "underline")}>
            {selectedPromptTitle}
          </span>
          <ChevronIcon
            className={cx("transition-transform", isOpen && "rotate-180")}
          />
        </Trigger>
      )}
    />
  );

  const automationsSection = (
    <div
      className={cx(
        "flex h-12 shrink-0 items-center justify-between border-0 border-solid border-grey-300 px-5 group-focus-within:border-dark-700",
        isDrawer ? "border-b" : "border-t"
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-secondary text-sm font-medium text-black">
          {t({
            id: "cx-ai-agents-page.automations",
            defaultMessage: "Automations",
          })}
        </span>
        <div className="flex h-[19px] items-center justify-center rounded-2xl border border-solid border-[#EEF2F5] px-2 font-secondary text-xs text-dark-700">
          {connectedAutomationsCount}
        </div>
      </div>
      {connectedAutomationsCount > 0 ? (
        <InteractiveContainer
          className="flex items-center"
          onClick={onAddAutomation}
          onMouseDown={(e) => e.preventDefault()}
        >
          {connectedAutomations.map(({ id, Icon }, index) => (
            <div
              key={id}
              className={cx(
                "relative flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-[#F2F2F2] bg-white",
                index < connectedAutomations.length - 1 && "-mr-[3px]"
              )}
            >
              <Icon />
            </div>
          ))}
        </InteractiveContainer>
      ) : (
        <Tooltip
          renderTrigger={({ props, ref }) => (
            <PlainButton
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
              ref={ref}
              {...props}
              onClick={onAddAutomation}
              onMouseDown={(e) => e.preventDefault()}
            >
              <AddIcon />
            </PlainButton>
          )}
        >
          {t({
            id: "cx-ai-agents-page.add-automation",
            defaultMessage: "Add automation",
          })}
        </Tooltip>
      )}
    </div>
  );

  return (
    <>
      <div
        className={cx(
          "flex flex-col gap-4 px-8",
          isDrawer &&
            "h-full min-h-0 gap-6 overflow-hidden px-8 py-8 lg:px-[127px]"
        )}
      >
        {isDrawer ? (
          <div className="flex items-center justify-between gap-3 px-2">
            <div className="flex items-center gap-3">
              <span className="font-secondary text-[20px] font-medium text-black">
                {t({
                  id: "cx-ai-agents-page.instructions-section-title",
                  defaultMessage: "✍️ Instructions",
                })}
              </span>
              {promptPickerTrigger}
            </div>
            {!isSelectedPromptAlreadyDefault && (
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
                    ref={ref}
                    {...props}
                    onClick={() => setIsResetConfirmOpen(true)}
                  >
                    <ResetIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "cx-ai-agents-page.reset-to-default-prompt",
                  defaultMessage: "Reset to default prompt",
                })}
              </Tooltip>
            )}
          </div>
        ) : (
          <div>
            <div className="flex h-8 items-center justify-between">
              <span className="font-secondary font-medium text-black">
                {t({
                  id: "cx-ai-agents-page.instructions-section-title",
                  defaultMessage: "✍️ Instructions",
                })}
              </span>
              <div className="flex gap-4">
                {!isSelectedPromptAlreadyDefault &&
                  selectedPromptOptionId !== "custom-prompt" && (
                    <Tooltip
                      renderTrigger={({ props, ref }) => (
                        <PlainButton
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
                          ref={ref}
                          {...props}
                          onClick={() => setIsResetConfirmOpen(true)}
                        >
                          <ResetIcon />
                        </PlainButton>
                      )}
                    >
                      {t({
                        id: "cx-ai-agents-page.reset-to-default-prompt",
                        defaultMessage: "Reset to default prompt",
                      })}
                    </Tooltip>
                  )}
                {onRequestFullscreen && (
                  <Tooltip
                    renderTrigger={({ props, ref }) => (
                      <PlainButton
                        ref={ref}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
                        {...props}
                        onClick={onRequestFullscreen}
                      >
                        <ExpandIcon />
                      </PlainButton>
                    )}
                  >
                    {t({
                      id: "cx-ai-agents-page.open-in-full-screen",
                      defaultMessage: "Open in full screen",
                    })}
                  </Tooltip>
                )}
              </div>
            </div>
            {promptPickerTrigger}
          </div>
        )}
        <Tooltip
          isOpen={!isDrawer && isTooltipOpen}
          placement="right"
          zIndex={10}
          className="w-[360px] max-w-full rounded-[20px] bg-[#333333] p-0"
          arrowClassName="fill-[#333333]"
          renderTrigger={({ props, ref }) => (
            <div
              className={cx(
                "group overflow-hidden rounded-xl border border-solid border-grey-300 transition-colors focus-within:border-dark-700",
                isDrawer
                  ? "flex min-h-0 flex-1 flex-col"
                  : "flex h-[360px] flex-col"
              )}
              ref={ref}
              {...props}
            >
              {isDrawer && automationsSection}
              <div
                ref={editorFieldsRef}
                className={cx(
                  "jugl__custom-scrollbar flex flex-col gap-3 overflow-y-auto py-5",
                  isDrawer ? "min-h-0 flex-1 px-5" : "min-h-0 flex-1"
                )}
                onBlur={handleEditorBlur}
              >
                <div
                  className={cx(
                    "flex shrink-0 cursor-text flex-col gap-1 px-5 py-0.5 transition-colors focus-within:bg-grey-100 hover:bg-grey-100",
                    isBusinessNameEmpty &&
                      "bg-tertiary/5 focus-within:bg-tertiary/5 hover:bg-tertiary/5"
                  )}
                  onClick={(e) => {
                    if (e.target === businessNameTextareaRef.current) return;
                    if (isBusinessNameEmpty && !isBusinessNameFocused) {
                      setIsBusinessNameFocused(true);
                      return;
                    }
                    businessNameTextareaRef.current?.focus();
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    {isBusinessNameEmpty && (
                      <Tooltip
                        isMouseOnly={false}
                        renderTrigger={({
                          props: tooltipProps,
                          ref: tooltipRef,
                        }) => (
                          <InfoIcon
                            ref={tooltipRef}
                            {...tooltipProps}
                            className="cursor-default"
                          />
                        )}
                      >
                        {t({
                          id: "cx-ai-agents-page.business-name-info",
                          defaultMessage:
                            "If the Business Name is not indicated - it might cause the misleading responses by AI Agent. Make sure to set proper name to avoid misunderstanding",
                        })}
                      </Tooltip>
                    )}
                    <span className="shrink-0 font-secondary text-sm leading-[140%] text-dark-700">
                      {t({
                        id: "cx-ai-agents-page.your-business-name-label",
                        defaultMessage: "Your Business Name:",
                      })}
                    </span>
                    {!isBusinessNameFocused && isBusinessNameEmpty && (
                      <span className="font-secondary text-sm text-dark-700">
                        {t({
                          id: "common.enter",
                          defaultMessage: "Enter",
                        })}
                      </span>
                    )}
                  </div>
                  {(isBusinessNameFocused || !isBusinessNameEmpty) && (
                    <textarea
                      ref={businessNameTextareaRef}
                      value={businessName}
                      onFocus={() => setIsBusinessNameFocused(true)}
                      onChange={(e) =>
                        setBusinessName(e.target.value.trimStart())
                      }
                      onBlur={() => {
                        setIsBusinessNameFocused(false);
                      }}
                      rows={1}
                      className="w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-secondary text-sm leading-[140%] text-black ring-0 outline-none focus:ring-0"
                    />
                  )}
                  {isBusinessNameEmpty && (
                    <span className="py-1 font-secondary text-xs leading-2 text-tertiary-700">
                      {t({
                        id: "cx-ai-agents-page.mandatory-field",
                        defaultMessage: "Mandatory field",
                      })}
                    </span>
                  )}
                </div>
                <div
                  className="flex cursor-text flex-col gap-0.5 px-5 py-0.5 transition-colors focus-within:bg-grey-100 hover:bg-grey-100"
                  onClick={() => instructionsTextareaRef.current?.focus()}
                >
                  <span className="shrink-0 font-secondary text-sm leading-[140%] text-dark-700">
                    {t({
                      id: "cx-ai-agents-page.general-instructions-label",
                      defaultMessage: "General Instructions:",
                    })}
                  </span>
                  <textarea
                    ref={instructionsTextareaRef}
                    value={instructions}
                    onChange={handleInstructionsChange}
                    rows={1}
                    className="w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-secondary text-sm leading-[140%] text-black ring-0 outline-none focus:ring-0"
                  />
                </div>
              </div>
              {!isDrawer && automationsSection}
            </div>
          )}
        >
          <div className="flex items-center justify-between gap-2 border-0 border-b border-solid border-dark-800 py-[3px] pl-6">
            <span className="font-secondary text-lg leading-[140%] font-semibold text-white">
              {t({
                id: "cx-ai-agents-page.business-intelligence-instructions-title",
                defaultMessage: "Business intelligence Instructions",
              })}
            </span>
            <Lottie
              options={{ animationData: brainAnimation }}
              width={78}
              height={78}
            />
          </div>
          <div className="px-6 py-2.5">
            <span className="font-secondary text-sm leading-[21px] text-white">
              {t({
                id: "cx-ai-agents-page.custom-instructions-info",
                defaultMessage:
                  "We prepared Custom Instructions generated for your Business based on provided Knowledge",
              })}
            </span>
          </div>
          <div className="flex h-10 items-center justify-end">
            <InteractiveContainer
              className="mx-8 mb-6 bg-[linear-gradient(149deg,#A9BDFF_68.46%,#F79FFF_93.9%)] bg-clip-text font-secondary text-sm leading-3 font-semibold text-transparent uppercase"
              onClick={() => setIsTooltipOpen(false)}
            >
              {t({
                id: "common.okay",
                defaultMessage: "Okay",
              })}
            </InteractiveContainer>
          </div>
        </Tooltip>
      </div>
      <Alert
        isOpen={isResetConfirmOpen}
        onRequestClose={() => setIsResetConfirmOpen(false)}
        title={t({
          id: "cx-ai-agents-page.reset-instructions-dialog-title",
          defaultMessage: "Reset to default",
        })}
        content={t(
          {
            id: "cx-ai-agents-page.reset-instructions-dialog-description",
            defaultMessage:
              "Are you sure you want to reset instructions to the default {agentName} prompt?",
          },
          {
            agentName: (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {selectedPromptTitle}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
            style: { flex: "none", width: "140px" },
          },
          {
            text: t({
              id: "common.reset",
              defaultMessage: "Reset",
            }),
            color: "primary",
            iconEnd: <ArrowIcon />,
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              handleResetPrompt();
            },
          },
        ]}
      />
    </>
  );
};
